import { createSelector } from 'reselect';

import { AppStateType } from '@lib/core/service/types/appStateType';
import { TProfileCountryOption, TProfileGenderOption } from '@lib/core/users/types';
import { languages } from '@lib/tools/locale/utils/consts';

export const selectProfileState = (state: AppStateType) => state.users.profile;

/**
 * @returns profile data
 */
export const selectProfileData = createSelector([selectProfileState], state => state?.data);

/**
 * @returns {boolean} is profile loading state
 */
export const selectIsProfileLoading: (state) => boolean = createSelector(
  [selectProfileState],
  state => state?.isLoading,
);

/**
 * @returns {number | null} the profile birth year
 */
export const selectProfileBirthYear: (state) => number | null = createSelector(
  [selectProfileData],
  state => state?.birth_year || null,
);

/**
 * @returns {string} the profile gender slug
 */
export const selectProfileGenderSlug: (state) => string = createSelector(
  [selectProfileData],
  state => state?.gender?.slug || '',
);

/**
 * @returns {string} the profile or user country
 */
export const selectProfileCountry = createSelector([selectProfileData], profileData => profileData?.country || '');

/**
 * @returns {string} the profile or user language
 */
export const selectProfileLanguage = createSelector(
  [selectProfileData],
  profileData => profileData?.language || languages.ENGLISH,
);

/**
 * @returns the profile Id
 */
export const selectProfileId: (state) => string = createSelector(
  [selectProfileState],
  state => state?.data?.identifier,
);

/**
 * @returns the profile user Id
 */
export const selectProfileUserId: (state) => string = createSelector(
  [selectProfileState],
  state => state?.data?.user?.identifier,
);

/**
 * @returns the profile gender options
 */
export const selectProfileGenderOptions: (state) => TProfileGenderOption[] = createSelector(
  [selectProfileState],
  state => state?.options?.genders,
);

/**
 * @returns the profile country options
 */
export const selectProfileCountryOptions: (state) => TProfileCountryOption[] = createSelector(
  [selectProfileState],
  state => state?.options?.countries,
);

/**
 * @returns the profile Language options
 */
export const selectProfileLanguageOptions: (state) => [string, string] = createSelector(
  [selectProfileState],
  state => state?.options?.languages,
);
